import { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import { Badge, CollapsibleSection, Pagination, RegionState } from '@freelancelabs/components';
import { BriefCaseOutline } from '@freelancelabs/icons';

import { AccountSocietiesQuery, AccountsDocument } from '../../graphql/__generated__/types';
import AccountsTable from './AccountsTable';
import { accountsRefusalOf } from './accountsRefusal';

type Society = NonNullable<NonNullable<AccountSocietiesQuery['accountSocieties']>[number]>;

const PAGE_SIZE = 25;

interface Props {
    society: Society;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Denominator of the "X / 6", read once per page from `portalModules`. */
    moduleTotal: number | null;
}

/**
 * A group header of the « Comptes » page: a parent company, its accounts on expand.
 *
 * The query only fires on expand (`skip: !open`): `<details>` keeps its panel in the DOM
 * even collapsed, so a mounted query would fire once per company on first render. The
 * header carries no link (no company sheet exists yet); the accounts link to their own.
 */
const AccountSocietyPanel = ({ society, open, onOpenChange, moduleTotal }: Props) => {
    const [page, setPage] = useState(1);

    const rootId = society.rootOrganizationId;

    const { data, loading, error } = useQuery(AccountsDocument, {
        variables: {
            rootId,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
        },
        skip: !open || !rootId,
        fetchPolicy: 'cache-and-network',
    });

    const rows = data?.accounts?.items ?? [];

    // The group counter comes from the index, never the expanded page: it counts the
    // whole company in scope, which `accounts(rootId:)` paginates.
    const total = society.accountCount ?? 0;
    const openCount = society.portalEnabledCount ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const countChip =
        society.accountCount === null || society.accountCount === undefined
            ? undefined
            : `${c('Account societies').ngettext(msgid`${total} account`, `${total} accounts`, total)} · ${c(
                  'Account societies'
              ).ngettext(msgid`${openCount} open`, `${openCount} open`, openCount)}`;

    const rangeStart = (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = (page - 1) * PAGE_SIZE + rows.length;
    const summary = c('Account societies').ngettext(
        msgid`${rangeStart}–${rangeEnd} of ${total} account`,
        `${rangeStart}–${rangeEnd} of ${total} accounts`,
        total
    );

    const refusal = error ? accountsRefusalOf(error) : null;

    // An empty panel under a header that announces accounts: index and page contradicted
    // each other, which the API documents for a company that left the perimeter (it
    // returns an empty page rather than a refusal). Said, so it does not read as "no account".
    const state = refusal ? (
        <RegionState tone="error" title={refusal.title} description={refusal.guidance} />
    ) : !loading && rows.length === 0 && open ? (
        <RegionState
            title={c('Account societies').t`This company came back with no account.`}
            description={c('Account societies')
                .t`Its counter says otherwise, so something changed between the two queries, or it is no longer within your perimeter.`}
        />
    ) : null;

    return (
        <CollapsibleSection
            className="fl-collapsible-section--group fl-collapsible-section--white-head"
            // The rail's « Comptes » icon, the same on every section; the name distinguishes.
            icon={<BriefCaseOutline size={16} />}
            open={open}
            onOpenChange={onOpenChange}
            title={society.rootOrganizationName ?? c('Account societies').t`Company with no name`}
            meta={
                <Badge category="default" size="small">
                    {society.siren
                        ? c('Account societies').t`SIREN ${society.siren}`
                        : c('Account societies').t`SIREN —`}
                </Badge>
            }
            count={countChip}
        >
            <AccountsTable
                rows={rows}
                moduleTotal={moduleTotal}
                nested
                loading={loading && rows.length === 0}
                hasError={Boolean(error)}
                state={state}
            />

            {/* No pager on an empty panel, an error, or a company that fits on one page. */}
            {!error && rows.length > 0 && pageCount > 1 && (
                <div className="px-4">
                    <Pagination
                        page={page}
                        pageSize={PAGE_SIZE}
                        totalCount={total}
                        summary={summary}
                        onPageChange={setPage}
                    />
                </div>
            )}
        </CollapsibleSection>
    );
};

export default AccountSocietyPanel;
